import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { canWrite } from '../../utils/roles';
import { formatDate } from '../../utils/format';
import './LeadsPage.css';

const STATUS = {
  nuevo:      { label: 'Nuevo',      color: '#5c91ff' },
  contactado: { label: 'Contactado', color: '#63bcd8' },
  calificado: { label: 'Calificado', color: '#a98fe0' },
  cotizado:   { label: 'Cotizado',   color: '#f59e0b' },
  ganado:     { label: 'Ganado',     color: '#22c55e' },
  perdido:    { label: 'Perdido',    color: '#ef4444' },
};
const STATUS_ORDER = ['nuevo', 'contactado', 'calificado', 'cotizado', 'ganado', 'perdido'];

const SOURCE = {
  manual:    { label: 'Manual',    icon: '✎' },
  web:       { label: 'Web',       icon: '🌐' },
  instagram: { label: 'Instagram', icon: '📷' },
  print:     { label: 'Captura',   icon: '🖼️' },
  whatsapp:  { label: 'WhatsApp',  icon: '💬' },
  otro:      { label: 'Otro',      icon: '•' },
};

const norm = s => (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const EMPTY = { source: 'manual', status: 'nuevo', contact_name: '', company: '', phone: '', email: '', instagram: '', event_type: '', event_date: '', place: '', guests: '', budget: '', message: '', notes: '' };

export default function LeadsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = canWrite(user, 'ventas.leads');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null); // lead | {} (nuevo) | null

  const load = useCallback(() => {
    setLoading(true);
    api.get('/leads')
      .then(r => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = rows;
    if (filter !== 'all') list = list.filter(r => r.status === filter);
    if (search.trim()) {
      const words = norm(search).split(/\s+/);
      list = list.filter(r => {
        const hay = norm(`${r.contact_name} ${r.company} ${r.event_type} ${r.place} ${r.phone} ${r.email} ${r.instagram} ${r.vendedor}`);
        return words.every(w => hay.includes(w));
      });
    }
    return list;
  }, [rows, search, filter]);

  const counts = useMemo(() => {
    const c = {};
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  async function convert(lead, e) {
    e.stopPropagation();
    if (lead.quote_id) { navigate(`/ventas/cotizaciones/${lead.quote_id}`); return; }
    if (!window.confirm(`Crear una cotización a partir de este lead (${lead.contact_name || lead.event_type || 's/nombre'})?`)) return;
    try {
      const { data } = await api.post(`/leads/${lead.id}/convert`);
      navigate(`/ventas/cotizaciones/${data.quote_id}`);
    } catch (err) {
      // Si ya estaba convertido (p. ej. doble click), el 409 trae el quote_id:
      // navegar a la cotización existente en vez de mostrar un error.
      const existing = err.response?.status === 409 && err.response.data?.quote_id;
      if (existing) { navigate(`/ventas/cotizaciones/${existing}`); return; }
      alert(err.response?.data?.error || 'No se pudo convertir el lead');
    }
  }

  return (
    <div className="page leads-page">
      <div className="page-header">
        <h1 className="page-title">Leads</h1>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setEditing({ ...EMPTY })}>+ Nuevo lead</button>
        )}
      </div>

      <div className="ld-toolbar">
        <input className="form-input ld-search" placeholder="Buscar por contacto, empresa o evento…" value={search} onChange={e => setSearch(e.target.value)} />
        <div className="ld-filter-actions" aria-label="Filtrar leads por estado">
          <button className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter('all')}>Todos</button>
          {STATUS_ORDER.map(k => (
            <button key={k} className={`btn ${filter === k ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter(k)}>
              {STATUS[k].label}{counts[k] ? ` ${counts[k]}` : ''}
            </button>
          ))}
        </div>
        <span className="ld-count">{filtered.length} lead{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="ld-table">
        <div className="ld-head">
          <span>Fuente</span><span>Contacto</span><span>Empresa</span><span>Evento</span>
          <span>Fecha</span><span>Vendedor</span><span>Estado</span><span></span>
        </div>
        {loading ? (
          <div className="ld-empty"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="ld-empty">{search || filter !== 'all' ? 'Sin resultados.' : 'No hay leads todavía. Cargá el primero con “+ Nuevo lead”.'}</div>
        ) : filtered.map(l => {
          const s = STATUS[l.status] || STATUS.nuevo;
          const src = SOURCE[l.source] || SOURCE.otro;
          return (
            <div key={l.id} className="ld-row" onClick={() => canEdit && setEditing(l)}>
              <span className="ld-cell" title={src.label}>{src.icon} <span className="ld-src">{src.label}</span></span>
              <span className="ld-cell ld-strong">{l.contact_name || '—'}</span>
              <span className="ld-cell">{l.company || '—'}</span>
              <span className="ld-cell">{l.event_type || '—'}</span>
              <span className="ld-cell">{formatDate(l.event_date) || '—'}</span>
              <span className="ld-cell">{l.vendedor || '—'}</span>
              <span className="ld-status" style={{ background: `${s.color}22`, color: s.color }}>{s.label}</span>
              <span className="ld-cell ld-actions" onClick={e => e.stopPropagation()}>
                {l.quote_id
                  ? <button className="ld-link" onClick={e => convert(l, e)} title="Ver cotización">{l.quote_folio || 'Ver cotización'}</button>
                  : canEdit && <button className="ld-convert" onClick={e => convert(l, e)}>→ Cotizar</button>}
              </span>
            </div>
          );
        })}
      </div>

      {editing && (
        <LeadModal
          lead={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function LeadModal({ lead, onClose, onSaved }) {
  const isNew = !lead.id;
  const [form, setForm] = useState({ ...EMPTY, ...lead, event_date: lead.event_date ? lead.event_date.slice(0, 10) : '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Rellenar con IA desde texto pegado y/o una captura.
  const [aiText, setAiText] = useState('');
  const [aiImg, setAiImg] = useState(null); // { dataUrl, name }
  const [aiLoading, setAiLoading] = useState(false);

  function pickImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAiImg({ dataUrl: reader.result, name: file.name });
    reader.readAsDataURL(file);
  }

  async function runAI() {
    if (!aiText.trim() && !aiImg) { alert('Pegá un mensaje o subí una captura primero.'); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post('/ai/extract-lead', { text: aiText, image: aiImg?.dataUrl });
      const f = data.fields || {};
      setForm(prev => {
        const next = { ...prev };
        for (const [k, v] of Object.entries(f)) {
          if (v == null || v === '') continue;
          next[k] = k === 'event_date' ? String(v).slice(0, 10) : v;
        }
        if (aiImg && (next.source === 'manual' || !next.source)) next.source = 'print';
        if (aiText && !aiImg && next.source === 'manual') next.source = 'whatsapp';
        return next;
      });
    } catch (err) {
      alert(err.response?.data?.error || 'La IA no pudo procesar esto.');
    } finally { setAiLoading(false); }
  }

  async function save() {
    if (!form.contact_name?.trim() && !form.company?.trim() && !form.event_type?.trim()) {
      alert('Poné al menos un contacto, empresa o tipo de evento.');
      return;
    }
    setSaving(true);
    try {
      if (isNew) await api.post('/leads', form);
      else await api.put(`/leads/${lead.id}`, form);
      onSaved();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar');
      setSaving(false);
    }
  }

  async function remove() {
    if (!window.confirm('¿Eliminar este lead?')) return;
    try { await api.delete(`/leads/${lead.id}`); onSaved(); }
    catch (err) { alert(err.response?.data?.error || 'No se pudo eliminar'); }
  }

  return (
    <div className="ld-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ld-modal">
        <div className="ld-modal-head">
          <h2>{isNew ? 'Nuevo lead' : 'Editar lead'}</h2>
          <button className="ld-x" onClick={onClose}>✕</button>
        </div>
        <div className="ld-ai">
          <div className="ld-ai-title">✨ Rellenar con IA <span>pegá el mensaje del cliente o subí una captura y completa los campos solo</span></div>
          <textarea className="input" rows={2} placeholder="Pegá acá el WhatsApp / DM de Instagram / mail del cliente…" value={aiText} onChange={e => setAiText(e.target.value)} />
          <div className="ld-ai-row">
            <label className="ld-ai-file">
              {aiImg ? `🖼️ ${aiImg.name}` : '📎 Subir captura'}
              <input type="file" accept="image/*" onChange={pickImage} hidden />
            </label>
            {aiImg && <button type="button" className="ld-ai-clear" onClick={() => setAiImg(null)}>quitar</button>}
            <button type="button" className="btn btn-primary ld-ai-btn" onClick={runAI} disabled={aiLoading}>
              {aiLoading ? 'Leyendo…' : '✨ Rellenar con IA'}
            </button>
          </div>
        </div>
        <div className="ld-form">
          <label className="ld-f"><span>Fuente</span>
            <select className="input" value={form.source} onChange={e => set('source', e.target.value)}>
              {Object.entries(SOURCE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </label>
          <label className="ld-f"><span>Estado</span>
            <select className="input" value={form.status} onChange={e => set('status', e.target.value)}>
              {STATUS_ORDER.map(k => <option key={k} value={k}>{STATUS[k].label}</option>)}
            </select>
          </label>
          <label className="ld-f"><span>Contacto</span><input className="input" value={form.contact_name || ''} onChange={e => set('contact_name', e.target.value)} placeholder="Nombre de quien consulta" /></label>
          <label className="ld-f"><span>Empresa / Cliente</span><input className="input" value={form.company || ''} onChange={e => set('company', e.target.value)} /></label>
          <label className="ld-f"><span>Teléfono</span><input className="input" value={form.phone || ''} onChange={e => set('phone', e.target.value)} /></label>
          <label className="ld-f"><span>Email</span><input className="input" value={form.email || ''} onChange={e => set('email', e.target.value)} /></label>
          <label className="ld-f"><span>Instagram</span><input className="input" value={form.instagram || ''} onChange={e => set('instagram', e.target.value)} placeholder="@usuario" /></label>
          <label className="ld-f"><span>Tipo de evento</span><input className="input" value={form.event_type || ''} onChange={e => set('event_type', e.target.value)} placeholder="Casamiento, corporativo…" /></label>
          <label className="ld-f"><span>Fecha tentativa</span><input className="input" type="date" value={form.event_date || ''} onChange={e => set('event_date', e.target.value)} /></label>
          <label className="ld-f"><span>Lugar</span><input className="input" value={form.place || ''} onChange={e => set('place', e.target.value)} /></label>
          <label className="ld-f"><span>Personas</span><input className="input" type="number" value={form.guests || ''} onChange={e => set('guests', e.target.value)} /></label>
          <label className="ld-f"><span>Presupuesto estimado</span><input className="input" type="number" value={form.budget || ''} onChange={e => set('budget', e.target.value)} /></label>
          <label className="ld-f ld-f-wide"><span>Consulta / mensaje</span><textarea className="input" rows={3} value={form.message || ''} onChange={e => set('message', e.target.value)} placeholder="El texto que mandó el cliente…" /></label>
          <label className="ld-f ld-f-wide"><span>Notas internas</span><textarea className="input" rows={2} value={form.notes || ''} onChange={e => set('notes', e.target.value)} /></label>
        </div>
        <div className="ld-modal-foot">
          {!isNew && <button className="ld-del" onClick={remove}>Eliminar</button>}
          <div className="ld-foot-right">
            <button className="btn" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
